import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api/axios';
import AgentLayout from './AgentLayout';

const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};

// Converts expires_at to YYYY-MM-DD so it can be compared with the date filter
const toISODate = (d) => {
  if (!d) return '';
  const str = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const dt = new Date(str);
  if (isNaN(dt)) return '';
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${m}-${day}`;
};

const PILL =
  'inline-flex items-center justify-center gap-1 whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-bold transition';
const PILL_GREEN = `${PILL} bg-[#ECFDF5] text-[#10B981] hover:bg-emerald-100`;
const PILL_TEAL = `${PILL} bg-teal-50 text-teal-600 hover:bg-teal-100`;
const PILL_DISABLED = `${PILL} bg-gray-50 text-gray-300 cursor-default`;

const FIELD =
  'w-full border border-gray-200 bg-gray-50/60 rounded-xl px-3 py-2.5 text-sm transition focus:outline-none focus:bg-white focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const FILTER_LABEL = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1';

const TIME_LEFT_OPTIONS = [
  { value: 'today', label: 'Expires today' },
  { value: 'le1', label: '1 day or less' },
  { value: 'le3', label: 'Up to 3 days' },
  { value: '4to6', label: '4 – 6 days' },
];

const matchesTimeLeft = (days, filter) => {
  if (!filter) return true;
  const d = Number(days);
  if (filter === 'today') return d <= 0;
  if (filter === 'le1') return d <= 1;
  if (filter === 'le3') return d <= 3;
  if (filter === '4to6') return d >= 4;
  return true;
};

// Searchable dropdown: type to filter the list, click to select
function SearchableSelect({ value, onChange, options, placeholder, allLabel }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  }, [options, query]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={open ? query : value}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onClick={() => setOpen(true)}
        onChange={(e) => { setOpen(true); setQuery(e.target.value); }}
        className={`${FIELD} pr-8`}
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[200px] max-h-64 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg">
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className="block w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-purple-50 hover:text-purple-600"
          >
            {allLabel}
          </button>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
          )}
          {filtered.map((o) => (
            <button
              type="button"
              key={o}
              onClick={() => { onChange(o); setOpen(false); }}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 ${
                o === value ? 'bg-purple-50 text-purple-700 font-semibold' : ''
              }`}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DaysLeftBadge({ days }) {
  const cls =
    days <= 1
      ? 'bg-red-50 text-red-600'
      : days <= 3
      ? 'bg-orange-50 text-orange-600'
      : 'bg-yellow-50 text-yellow-700';
  const text = days <= 0 ? 'Expires today' : days === 1 ? '1 day left' : `${days} days left`;
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`}>
      {text}
    </span>
  );
}

function CompanyProfileModal({ sub, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Optional: fetch the full profile if the row has an employer_id
  useEffect(() => {
    if (!sub.employer_id) return;
    setLoading(true);
    api
      .get(`/agent/employers/${sub.employer_id}`)
      .then((res) => setProfile(res.data.employer || res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [sub]);

  const p = profile || {};
  const name = p.company_name || p.name || sub.employer_name;
  const rows = [
    ['Email', p.email || sub.email],
    ['Phone', p.phone || sub.phone],
    ['Website', p.website],
    ['Industry', p.industry],
    ['Location', p.location || p.city],
    ['Company Size', p.company_size],
  ].filter(([, v]) => v);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white flex items-center justify-center text-lg font-bold">
              {(name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">{name}</h3>
              <p className="text-xs text-gray-400">Company Profile</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-purple-600 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading && <p className="text-sm text-gray-400">Loading profile…</p>}

          <div className="space-y-2.5">
            {rows.map(([label, value]) => (
              <div key={label} className="flex text-sm">
                <span className="w-32 shrink-0 text-gray-400">{label}</span>
                <span className="text-gray-700 break-words">{value}</span>
              </div>
            ))}
          </div>

          {p.about && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">About</p>
              <p className="text-sm text-gray-600 whitespace-pre-line">{p.about}</p>
            </div>
          )}

          <div className="bg-[#FDF5FF] rounded-xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Subscription</p>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-gray-400">Plan</span>
              <span className="text-gray-700 capitalize">{sub.plan_name}</span>
              <span className="text-gray-400">Amount</span>
              <span className="text-gray-700">{fmtINR(sub.amount)}</span>
              <span className="text-gray-400">Cycle</span>
              <span className="text-gray-700 capitalize">{sub.billing_cycle ?? '—'}</span>
              <span className="text-gray-400">Auto-pay</span>
              <span className="text-gray-700">{sub.auto_renew ? 'On' : 'Off'}</span>
              <span className="text-gray-400">Expires</span>
              <span className="text-gray-700">{sub.expires_at}</span>
              <span className="text-gray-400">Time left</span>
              <span><DaysLeftBadge days={sub.days_left} /></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentExpiringPlans() {
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [plans, setPlans] = useState([]);

  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [cycleFilter, setCycleFilter] = useState('');
  const [autoPayFilter, setAutoPayFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
const [dateTo, setDateTo] = useState('');
  const [timeLeftFilter, setTimeLeftFilter] = useState('');
const [selectedSub, setSelectedSub] = useState(null);
  const load = async () => {
    setStatus('loading');
    try {
      const res = await api.get('/agent/expiring-plans', { params: { days: 6 } });
      setPlans(res.data.subscriptions || []);
      setStatus('ok');
    } catch (err) {
      console.error('Failed to load expiring plans', err);
      setStatus('error');
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Dropdown options built from the loaded data
  const companyOptions = useMemo(
    () => Array.from(new Set(plans.map((p) => p.employer_name).filter(Boolean))).sort(),
    [plans]
  );
  const planOptions = useMemo(
    () => Array.from(new Set(plans.map((p) => p.plan_name).filter(Boolean))).sort(),
    [plans]
  );
  const cycleOptions = useMemo(
    () => Array.from(new Set(plans.map((p) => p.billing_cycle).filter(Boolean))).sort(),
    [plans]
  );

  const filtered = plans.filter((s) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      s.employer_name?.toLowerCase().includes(q) ||
      s.plan_name?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.phone?.includes(q);

    const matchesCompany = !companyFilter || s.employer_name === companyFilter;
    const matchesPlan = !planFilter || s.plan_name === planFilter;
    const matchesCycle = !cycleFilter || s.billing_cycle === cycleFilter;
    const matchesAutoPay =
      !autoPayFilter ||
      (autoPayFilter === 'on' && !!s.auto_renew) ||
      (autoPayFilter === 'off' && !s.auto_renew);
    const exp = toISODate(s.expires_at);
const matchesDate =
  (!dateFrom || (exp && exp >= dateFrom)) &&
  (!dateTo || (exp && exp <= dateTo));

    return (
      matchesSearch &&
      matchesCompany &&
      matchesPlan &&
      matchesCycle &&
      matchesAutoPay &&
      matchesDate &&
      matchesTimeLeft(s.days_left, timeLeftFilter)
    );
  });

  const hasFilters =
  search || companyFilter || planFilter || cycleFilter || autoPayFilter || dateFrom || dateTo || timeLeftFilter;

  const clearAll = () => {
    setSearch('');
    setCompanyFilter('');
    setPlanFilter('');
    setCycleFilter('');
    setAutoPayFilter('');
   setDateFrom('');
setDateTo('');
    setTimeLeftFilter('');
  };

  return (
    <AgentLayout>
      <div className="bg-[#FDF5FF] -m-6 p-6 min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
              Expiring Plans
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Employers whose plan expires within the next 6 days. Contact them to renew.
            </p>
          </div>
        </div>

        {status === 'error' && (
          <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl p-4 mb-6">
            Could not load expiring plans.{' '}
            <button onClick={load} className="underline font-medium">
              Retry
            </button>
          </div>
        )}

        {status === 'ok' && plans.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
            {/* Search */}
            <div className="relative mb-4">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                🔍
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by employer, plan, email or phone..."
                className={`${FIELD} pl-10 pr-10`}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-600 text-lg leading-none"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            {/* Filters */}
        {/* Filters */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
  <div>
    <label className={FILTER_LABEL}>Company</label>
    <SearchableSelect
      value={companyFilter}
      onChange={setCompanyFilter}
      options={companyOptions}
      placeholder="All Companies"
      allLabel="All Companies"
    />
  </div>

  <div>
    <label className={FILTER_LABEL}>Plan</label>
    <select
      value={planFilter}
      onChange={(e) => setPlanFilter(e.target.value)}
      className={`${FIELD} cursor-pointer capitalize`}
    >
      <option value="">All Plans</option>
      {planOptions.map((p) => (
        <option key={p} value={p}>{p}</option>
      ))}
    </select>
  </div>

  <div>
    <label className={FILTER_LABEL}>Cycle</label>
    <select
      value={cycleFilter}
      onChange={(e) => setCycleFilter(e.target.value)}
      className={`${FIELD} cursor-pointer capitalize`}
    >
      <option value="">All Cycles</option>
      {cycleOptions.map((c) => (
        <option key={c} value={c}>{c}</option>
      ))}
    </select>
  </div>

  <div>
    <label className={FILTER_LABEL}>Auto-pay</label>
    <select
      value={autoPayFilter}
      onChange={(e) => setAutoPayFilter(e.target.value)}
      className={`${FIELD} cursor-pointer`}
    >
      <option value="">All</option>
      <option value="on">On</option>
      <option value="off">Off</option>
    </select>
  </div>

  <div>
    <label className={FILTER_LABEL}>Expires From</label>
    <input
      type="date"
      value={dateFrom}
      max={dateTo || undefined}
      onChange={(e) => setDateFrom(e.target.value)}
      className={FIELD}
    />
  </div>

  <div>
    <label className={FILTER_LABEL}>Expires To</label>
    <input
      type="date"
      value={dateTo}
      min={dateFrom || undefined}
      onChange={(e) => setDateTo(e.target.value)}
      className={FIELD}
    />
  </div>

  <div>
    <label className={FILTER_LABEL}>Time Left</label>
    <select
      value={timeLeftFilter}
      onChange={(e) => setTimeLeftFilter(e.target.value)}
      className={`${FIELD} cursor-pointer`}
    >
      <option value="">Any</option>
      {TIME_LEFT_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
</div>
            {/* Result count + clear */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
              <span>
                Showing <b className="text-purple-700">{filtered.length}</b> of {plans.length} plans
              </span>
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="font-semibold text-purple-600 hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead>
                <tr className="text-left text-gray-400 text-xs uppercase bg-gray-50/60">
                  <th className="px-5 pt-4 pb-2 font-medium">Employer</th>
                  <th className="px-5 pt-4 pb-2 font-medium">Email</th>
                  <th className="px-5 pt-4 pb-2 font-medium">Phone</th>
                  <th className="px-5 pt-4 pb-2 font-medium">Plan</th>
                  <th className="px-5 pt-4 pb-2 font-medium">Amount</th>
                  <th className="px-5 pt-4 pb-2 font-medium">Cycle</th>
                  <th className="px-5 pt-4 pb-2 font-medium">Auto-pay</th>
                  <th className="px-5 pt-4 pb-2 font-medium">Expires</th>
                  <th
  className="px-5 pt-4 pb-2 font-medium sticky z-20 bg-gray-50 w-[140px] min-w-[140px]"
  style={{ right: 250 }}
>
  Time left
</th>
<th className="px-5 pt-4 pb-2 font-medium text-right sticky right-0 z-20 bg-gray-50 border-l border-gray-100 w-[250px] min-w-[250px] shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
  Action
</th>
                </tr>
              </thead>
              <tbody>
                {status === 'loading' && (
                  <tr>
                    <td colSpan={10} className="py-6 text-center text-gray-400 text-sm">
                      Loading…
                    </td>
                  </tr>
                )}
                {status === 'ok' && plans.length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-6 text-center text-gray-400 text-sm">
                      No employer plans are expiring in the next 6 days.
                    </td>
                  </tr>
                )}
                {status === 'ok' && plans.length > 0 && filtered.length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-6 text-center text-gray-400 text-sm">
                      No results match your search or filters.
                    </td>
                  </tr>
                )}
                {filtered.map((s) => (
                  <tr key={s.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                    <td className="px-5 py-2.5 font-medium">
  <button
    type="button"
    onClick={() => setSelectedSub(s)}
    className="text-purple-700 hover:text-pink-600 hover:underline text-left"
  >
    {s.employer_name}
  </button>
</td>
                    <td className="px-5 py-2.5">
                      {s.email ? (
                        <a
                          href={`mailto:${s.email}`}
                          className="text-purple-600 hover:text-pink-600 hover:underline"
                        >
                          {s.email}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 whitespace-nowrap">
                      {s.phone ? (
                        <a
                          href={`tel:${s.phone}`}
                          className="text-purple-600 hover:text-pink-600 hover:underline"
                        >
                          {s.phone}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 capitalize">{s.plan_name}</td>
                    <td className="px-5 py-2.5">{fmtINR(s.amount)}</td>
                    <td className="px-5 py-2.5 capitalize">{s.billing_cycle ?? '—'}</td>
                    <td className="px-5 py-2.5">
                      {s.auto_renew ? (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600">On</span>
                      ) : (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">Off</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-gray-500 whitespace-nowrap">{s.expires_at}</td>
                   <td
  className="px-5 py-2.5 sticky z-10 bg-white w-[140px] min-w-[140px]"
  style={{ right: 250 }}
>
  <DaysLeftBadge days={s.days_left} />
</td>
<td className="px-5 py-2.5 sticky right-0 z-10 bg-white border-l border-gray-100 w-[250px] min-w-[250px] shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
  <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                        {s.phone ? (
                          <a href={`tel:${s.phone}`} className={PILL_GREEN}>📞 Call</a>
                        ) : (
                          <span className={PILL_DISABLED}>📞 Call</span>
                        )}
                        {waLink(s.phone) ? (
                          <a
                            href={waLink(s.phone)}
                            target="_blank"
                            rel="noreferrer"
                            className={PILL_TEAL}
                          >
                            💬 WhatsApp
                          </a>
                        ) : (
                          <span className={PILL_DISABLED}>💬 WhatsApp</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedSub && (
        <CompanyProfileModal sub={selectedSub} onClose={() => setSelectedSub(null)} />
      )}
    </AgentLayout>
  );
}

export default AgentExpiringPlans;